import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import OnlineUsersBadge from './OnlineUsersBadge';
import { FaUser, FaSignOutAlt, FaSync, FaSearch, FaBars, FaTimes, FaChevronDown, FaChevronRight, FaChevronLeft, FaHome, FaClipboardList, FaUserCircle } from 'react-icons/fa';
import { getNavigationByRole } from '../config/navigation';
import usePwaDisplayMode from '../hooks/usePwaDisplayMode';
import '../css/template-navbar.css';

const DESKTOP_SIDEBAR_MEDIA_QUERY = '(min-width: 769px)';

const getIsDesktopViewport = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }

  return window.matchMedia(DESKTOP_SIDEBAR_MEDIA_QUERY).matches;
};

const ModernNavbar = () => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(getIsDesktopViewport);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const userMenuRef = useRef(null);
  const pwaSheetRef = useRef(null);
  const navSectionsRef = useRef([]);

  // Touch handling for mobile swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const { isPwaExperience } = usePwaDisplayMode();
  const isDesktopMiniMode = Boolean(user && !isPwaExperience && isDesktopViewport);

  // Navigation structure from shared config
  const navigationStructure = {
    EMPLOYEE: getNavigationByRole('EMPLOYEE'),
    LEVEL_I_MANAGER: getNavigationByRole('LEVEL_I_MANAGER'),
    LEVEL_II_MANAGER: getNavigationByRole('LEVEL_II_MANAGER'),
    ACCOUNTANT: getNavigationByRole('ACCOUNTANT'),
    REPORT_MANAGER: getNavigationByRole('REPORT_MANAGER'),
    ADMIN: getNavigationByRole('ADMIN'),
    SUPER_ADMIN: getNavigationByRole('SUPER_ADMIN'),
  };

  // Common links that appear for all users
  const commonLinks = [
    { to: '/', label: 'Trang chủ', icon: 'fa-home' },
    { to: '/ai-chat', label: 'AI Chat', icon: 'fa-robot' },
    { to: '/todos', label: 'Sổ tay việc làm', icon: 'fa-tasks' }
  ];

  // Utility: normalize Vietnamese text for accent-insensitive search
  // - Normalize to NFD, remove combining diacritics \u0300-\u036f
  // - Replace đ/Đ with d/D
  // - Lowercase
  const normalizeText = (text) =>
    (text || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
      .toLowerCase();

  // Get user navigation before useEffect
  const userNavigation = navigationStructure[user?.role] || [];

  // Track viewport changes for desktop mini sidebar behavior
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(DESKTOP_SIDEBAR_MEDIA_QUERY);
    const handleViewportChange = (event) => {
      setIsDesktopViewport(event.matches);
    };

    setIsDesktopViewport(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleViewportChange);
    } else {
      mediaQuery.addListener(handleViewportChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleViewportChange);
      } else {
        mediaQuery.removeListener(handleViewportChange);
      }
    };
  }, []);

  // Body classes reserve horizontal space for desktop mini/expanded sidebar
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const body = document.body;
    body.classList.toggle('has-desktop-sidebar', isDesktopMiniMode);
    body.classList.toggle('has-expanded-sidebar', isDesktopMiniMode && sidebarOpen);
    body.classList.toggle('desktop-sidebar-no-header', isDesktopMiniMode);

    return () => {
      body.classList.remove('has-desktop-sidebar');
      body.classList.remove('has-expanded-sidebar');
      body.classList.remove('desktop-sidebar-no-header');
    };
  }, [isDesktopMiniMode, sidebarOpen]);

  // Handle touch swipe to close sidebar
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    // Prevent horizontal scrolling within sidebar content
    const target = e.target;
    const sidebarContent = target.closest('.sidebar-content');

    if (sidebarContent) {
      const currentX = e.targetTouches[0].clientX;
      const currentY = e.targetTouches[0].clientY;

      if (touchStart) {
        const deltaX = Math.abs(currentX - touchStart);
        const deltaY = Math.abs(currentY - (e.targetTouches[0].clientY || touchStart));

        // If horizontal movement is greater than vertical, prevent default
        if (deltaX > deltaY && deltaX > 10) {
          e.preventDefault();
        }
      }
    }

    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    // Close sidebar on left swipe when sidebar is open
    if (isLeftSwipe && sidebarOpen) {
      setSidebarOpen(false);
      setExpandedGroups({});
    }
  };

  // Close sidebar and reset when user logs out
  useEffect(() => {
    if (!user) {
      setSidebarOpen(false);
      setExpandedGroups({});
      setSearchQuery('');
    }
  }, [user]);

  // Auto-adjust flyout position based on screen position
  useEffect(() => {
    const adjustFlyoutPositions = () => {
      if (!sidebarOpen) return;

      const navSections = document.querySelectorAll('.modern-navbar-container .nav-section:not(.common-section):not(.support-section)');
      const sidebarContent = document.querySelector('.modern-navbar-container .sidebar-content');

      if (!sidebarContent) return;

      const sidebarContentRect = sidebarContent.getBoundingClientRect();
      const flyoutHeight = 250; // Estimated flyout height

      navSections.forEach((section) => {
        const rect = section.getBoundingClientRect();

        // Calculate position relative to sidebar content
        const sectionBottom = rect.bottom;
        const sidebarBottom = sidebarContentRect.bottom;
        const availableSpace = sidebarBottom - sectionBottom;

        // If not enough space below for flyout, show it above
        if (availableSpace < flyoutHeight && rect.top > sidebarContentRect.top + flyoutHeight) {
          section.classList.add('flyout-up');
        } else {
          section.classList.remove('flyout-up');
        }
      });
    };

    if (sidebarOpen) {
      // Adjust positions when sidebar opens
      const timeoutId = setTimeout(adjustFlyoutPositions, 150);

      // Adjust on scroll within sidebar
      const sidebarContent = document.querySelector('.modern-navbar-container .sidebar-content');
      if (sidebarContent) {
        sidebarContent.addEventListener('scroll', adjustFlyoutPositions);

        // Also adjust on window resize
        window.addEventListener('resize', adjustFlyoutPositions);

        return () => {
          clearTimeout(timeoutId);
          sidebarContent.removeEventListener('scroll', adjustFlyoutPositions);
          window.removeEventListener('resize', adjustFlyoutPositions);
        };
      }

      return () => clearTimeout(timeoutId);
    }
  }, [sidebarOpen, userNavigation, searchQuery]);

  // Close sidebar when clicking outside and handle keyboard shortcuts
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
        // Close all expanded sections when sidebar closes
        setExpandedGroups({});
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target) &&
          (!pwaSheetRef.current || !pwaSheetRef.current.contains(event.target))) {
        setUserMenuOpen(false);
      }

      // Handle clicks inside sidebar
      if (sidebarRef.current && sidebarRef.current.contains(event.target)) {
        const clickedNavItem = event.target.closest('.nav-item');
        const clickedSectionTitle = event.target.closest('.section-title.expandable');
        const clickedInSidebarContent = event.target.closest('.sidebar-content');

        // If clicked on a nav-item (menu item), navigation will handle closing
        // If clicked on section title, toggleGroup will handle it
        // If clicked elsewhere in sidebar content (but not on section titles), close expanded sections
        if (!clickedNavItem && !clickedSectionTitle && clickedInSidebarContent) {
          setExpandedGroups({});
        }
      }
    };

    const handleKeyDown = (event) => {
      // ESC key to close expanded sections, clear search, or close sidebar
      if (event.key === 'Escape') {
        const hasExpandedSections = Object.values(expandedGroups).some(expanded => expanded);

        if (hasExpandedSections) {
          // First ESC: Close expanded sections
          setExpandedGroups({});
        } else if (searchQuery) {
          // Second ESC: Clear search
          setSearchQuery('');
        } else if (sidebarOpen) {
          // Third ESC: Close sidebar and expanded sections
          setSidebarOpen(false);
          setExpandedGroups({});
        }
      }

      // Ctrl/Cmd + K to open sidebar and focus search (only when logged in)
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        if (!user) return;
        event.preventDefault();
        setSidebarOpen(true);
        setTimeout(() => {
          const searchInput = document.querySelector('.modern-navbar-container .search-input');
          if (searchInput) searchInput.focus();
        }, 100);
      }
    };

    // Use both mousedown and touchstart for better mobile support
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchQuery, sidebarOpen, expandedGroups]);

  // Handle logout
  const handleLogout = () => {
    logout();
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/login');
    window.location.reload();
  };

  // Handle refresh
  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prevOpen) => {
      const nextOpen = !prevOpen;
      if (!nextOpen) {
        setExpandedGroups({});
      }
      return nextOpen;
    });
  };

  // Toggle group expansion
  const toggleGroup = (groupIndex) => {
    setExpandedGroups(prev => {
      const isCurrentlyOpen = prev[groupIndex];

      // Nếu đang mở thì đóng lại
      if (isCurrentlyOpen) {
        return {
          ...prev,
          [groupIndex]: false
        };
      }

      // Nếu đang đóng thì đóng tất cả và chỉ mở cái này
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      newState[groupIndex] = true;

      return newState;
    });
  };

  // Filter navigation items based on search (accent-insensitive)
  const filterItems = (items, query) => {
    if (!query) return items;
    const q = normalizeText(query);
    return items.filter(item => normalizeText(item.label).includes(q));
  };

  const renderUserDropdown = (variant = 'desktop') => {
    if (!userMenuOpen) return null;

    if (variant === 'pwa') {
      return createPortal(
        <>
          <div className="pwa-sheet-overlay" onClick={() => setUserMenuOpen(false)} />
          <div className="pwa-user-sheet" ref={pwaSheetRef}>
            <div className="pwa-sheet-handle" />
            <div className="pwa-sheet-header">
              {user.avatar ? (
                <img src={user.avatar} alt="Avatar" className="pwa-sheet-avatar" />
              ) : (
                <FaUserCircle className="pwa-sheet-avatar-icon" />
              )}
              <div className="pwa-sheet-user-info">
                <span className="pwa-sheet-user-name">{user.name || 'Người dùng'}</span>
                <span className="pwa-sheet-user-role">{user.role || ''}</span>
              </div>
            </div>
            <div className="pwa-sheet-divider" />
            <div className="pwa-sheet-items">
              <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/profile', e); }} className="pwa-sheet-item">
                <FaUser /> <span>Thông tin cá nhân</span>
              </button>
              <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/notification-settings', e); }} className="pwa-sheet-item">
                <i className="fas fa-bell"></i> <span>Cài đặt thông báo</span>
              </button>
              <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/driver-profiles', e); }} className="pwa-sheet-item">
                <i className="fas fa-id-card"></i> <span>Hồ sơ tài xế</span>
              </button>
              <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/driver-schedule', e); }} className="pwa-sheet-item">
                <i className="fas fa-calendar-alt"></i> <span>Lịch trình tài xế</span>
              </button>
              {['ADMIN', 'SUPER_ADMIN', 'LEVEL_I_MANAGER', 'LEVEL_II_MANAGER', 'ACCOUNTANT', 'REPORT_MANAGER'].includes(user?.role) && (
                <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/all-driver-schedules', e); }} className="pwa-sheet-item">
                  <i className="fas fa-calendar-check"></i> <span>Quản lý lịch trình tài xế</span>
                </button>
              )}
              {['ADMIN', 'SUPER_ADMIN', 'LEVEL_II_MANAGER'].includes(user?.role) && (
                <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/pickup-schedules', e); }} className="pwa-sheet-item">
                  <i className="fas fa-calendar-check"></i> <span>Lịch đón khách</span>
                </button>
              )}
            </div>
            <div className="pwa-sheet-divider" />
            <button onClick={handleLogout} className="pwa-sheet-item pwa-sheet-logout">
              <FaSignOutAlt /> <span>Đăng xuất</span>
            </button>
          </div>
        </>,
        document.body
      );
    }

    return (
      <div className={`user-dropdown ${variant === 'sidebar' ? 'sidebar-user-dropdown' : variant === 'sidebar-collapsed' ? 'sidebar-collapsed-user-dropdown' : ''}`}>
        <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/profile', e); }} className="dropdown-item">
          <FaUser /> Thông tin cá nhân
        </button>
        <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/notification-settings', e); }} className="dropdown-item">
          <i className="fas fa-bell"></i> Cài đặt thông báo
        </button>
        <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/driver-profiles', e); }} className="dropdown-item">
          <i className="fas fa-id-card"></i> Hồ sơ tài xế
        </button>
        <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/driver-schedule', e); }} className="dropdown-item">
          <i className="fas fa-calendar-alt"></i> Lịch trình tài xế
        </button>
        {['ADMIN', 'SUPER_ADMIN', 'LEVEL_I_MANAGER', 'LEVEL_II_MANAGER', 'ACCOUNTANT', 'REPORT_MANAGER'].includes(user?.role) && (
          <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/all-driver-schedules', e); }} className="dropdown-item">
            <i className="fas fa-calendar-check"></i> Quản lý lịch trình tài xế
          </button>
        )}
        {['ADMIN', 'SUPER_ADMIN', 'LEVEL_II_MANAGER'].includes(user?.role) && (
          <button onClick={(e) => { setUserMenuOpen(false); handleNavigation('/pickup-schedules', e); }} className="dropdown-item">
            <i className="fas fa-calendar-check"></i> Lịch đón khách
          </button>
        )}
        <div className="dropdown-divider"></div>
        <button onClick={handleLogout} className="dropdown-item logout">
          <FaSignOutAlt /> Đăng xuất
        </button>
      </div>
    );
  };

  const renderPwaBottomNav = () => {
    const isActive = (path) => {
      if (!path) return false;
      if (path === '/') {
        return location.pathname === '/';
      }
      return location.pathname.startsWith(path);
    };

    return (
      <div className="pwa-bottom-nav" role="navigation">
        <button
          type="button"
          className={`pwa-nav-item ${isActive('/') ? 'active' : ''}`}
          onClick={(e) => handleNavigation('/', e)}
        >
          <FaHome className="pwa-nav-icon" />
          <span>Trang chủ</span>
        </button>

        {user ? (
          <>
            <button
              type="button"
              className={`pwa-nav-item ${isActive('/todos') ? 'active' : ''}`}
              onClick={(e) => handleNavigation('/todos', e)}
            >
              <FaClipboardList className="pwa-nav-icon" />
              <span>Việc làm</span>
            </button>

            <div className="pwa-nav-item pwa-nav-notification">
              <NotificationBell />
            </div>

            <div className="pwa-nav-item pwa-user-nav" ref={userMenuRef}>
              <button
                type="button"
                className="pwa-nav-trigger"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                {user.avatar ? (
                  <img src={user.avatar} alt="Avatar" className="pwa-nav-avatar" />
                ) : (
                  <FaUserCircle className="pwa-nav-icon" />
                )}
                <span>Tài khoản</span>
              </button>
              {renderUserDropdown('pwa')}
            </div>

            <button
              type="button"
              className={`pwa-nav-item ${sidebarOpen ? 'active' : ''}`}
              onClick={handleSidebarToggle}
            >
              {sidebarOpen ? <FaTimes className="pwa-nav-icon" /> : <FaBars className="pwa-nav-icon" />}
              <span>Menu</span>
            </button>
          </>
        ) : (
          <button
            type="button"
            className={`pwa-nav-item ${isActive('/login') ? 'active' : ''}`}
            onClick={(e) => handleNavigation('/login', e)}
          >
            <FaUser className="pwa-nav-icon" />
            <span>Đăng nhập</span>
          </button>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!isNavigating) return undefined;

    const timer = setTimeout(() => {
      setIsNavigating(false);
    }, 250);

    return () => clearTimeout(timer);
  }, [location.pathname, isNavigating]);

  // Handle navigation without full page reload
  const handleNavigation = (path, event) => {
    // Prevent event bubbling to avoid triggering other click handlers
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setSidebarOpen(false);
    setExpandedGroups({}); // Explicitly close all expanded groups
    if (location.pathname === path) {
      setIsNavigating(false);
      return;
    }

    setIsNavigating(true);
    navigate(path);
  };

  return (
    <div className={`modern-navbar-container ${isPwaExperience ? 'pwa-mode' : ''}`}>
      {!isPwaExperience && !isDesktopMiniMode && (
        <>
          {/* Top Header */}
          <header className="modern-header">
            <div className="header-content">
              {/* Logo */}
              <div className="header-logo">
                <button
                  className="logo-link"
                  onClick={(e) => handleNavigation('/', e)}
                >
                  <img src="/img/logo.png" alt="Logo" className="logo-image" />
                  <div className="brand-info">
                    <span className="brand-primary">Công Ty CPTMQT</span>
                    <span className="brand-secondary">Tân Đại Tây Dương</span>
                  </div>
                </button>
              </div>

              {/* Header Actions */}
              <div className="header-actions">
                {user && (
                  <>
                    <div className="header-icon-wrapper notification-wrapper">
                      <NotificationBell />
                    </div>
                    {user.role === 'SUPER_ADMIN' && (
                      <div className="header-icon-wrapper online-users-badge-wrapper">
                        <OnlineUsersBadge />
                      </div>
                    )}

                    {/* Refresh Button */}
                    <button
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      className={`action-btn refresh-btn ${isRefreshing ? 'refreshing' : ''}`}
                      title="Làm mới trang"
                    >
                      <FaSync className={isRefreshing ? 'fa-spin' : ''} />
                    </button>

                    {/* User Menu */}
                    <div className="user-menu-container" ref={userMenuRef}>
                      <button
                        className="user-avatar-btn"
                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                      >
                        {user.avatar ? (
                          <img src={user.avatar} alt="Avatar" className="user-avatar" />
                        ) : (
                          <div className="user-avatar-placeholder">
                            {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                          </div>
                        )}
                        <span className="user-name">{user.fullName || 'User'}</span>
                        <FaChevronDown className={`chevron ${userMenuOpen ? 'open' : ''}`} />
                      </button>

                      {renderUserDropdown()}
                    </div>
                  </>
                )}

                {/* Menu Toggle - only on mobile/tablet header */}
                {user && !isDesktopMiniMode && (
                  <button
                    type="button"
                    className={`menu-toggle ${sidebarOpen ? 'is-open' : 'is-closed'}`}
                    onClick={handleSidebarToggle}
                    title={sidebarOpen ? 'Đóng menu điều hướng' : 'Mở menu điều hướng'}
                    aria-label={sidebarOpen ? 'Đóng menu điều hướng' : 'Mở menu điều hướng'}
                    aria-expanded={sidebarOpen}
                  >
                    {sidebarOpen ? <FaTimes /> : <FaBars />}
                  </button>
                )}
              </div>
            </div>
          </header>
        </>
      )}

      {isPwaExperience && renderPwaBottomNav()}

      {/* Sidebar Overlay */}
      {sidebarOpen && !isDesktopMiniMode && <div className="sidebar-overlay" onClick={() => {
        setSidebarOpen(false);
        setExpandedGroups({});
      }} />}

      {/* Navigation Loading Overlay */}
      {isNavigating && (
        <div className="navigation-loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <span>Đang chuyển trang...</span>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <nav
        id="app-sidebar-navigation"
        className={`modern-sidebar ${sidebarOpen ? 'open' : ''} ${isDesktopMiniMode ? 'desktop-mini' : ''}`}
        ref={sidebarRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        {isDesktopMiniMode && !sidebarOpen && (
          <button
            type="button"
            className="sidebar-desktop-toggle is-closed"
            onClick={handleSidebarToggle}
            aria-controls="app-sidebar-navigation"
            aria-expanded={false}
            aria-pressed={false}
            title="Mở rộng menu"
            aria-label="Mở rộng menu điều hướng"
          >
            <FaChevronRight />
          </button>
        )}

        {isDesktopMiniMode && user && !sidebarOpen && (
          <button
            className="sidebar-collapsed-logo"
            onClick={(e) => handleNavigation('/', e)}
            title="Trang ch\u1ee7"
            aria-label="Trang ch\u1ee7"
          >
            <img src="/img/logo.png" alt="Logo" className="logo-image" />
          </button>
        )}

        {/* Floating Notification Bell — visible when sidebar collapsed */}
        {isDesktopMiniMode && user && !sidebarOpen && (
          <div className="sidebar-collapsed-bell">
            <NotificationBell />
          </div>
        )}

        {isDesktopMiniMode && user && (
          <div className="sidebar-desktop-top">
            <button
              className="logo-link sidebar-desktop-logo"
              onClick={(e) => handleNavigation('/', e)}
            >
              <img src="/img/logo.png" alt="Logo" className="logo-image" />
              <div className="brand-info">
                <span className="brand-primary">Công Ty CPTMQT</span>
                <span className="brand-secondary">Tân Đại Tây Dương</span>
              </div>
            </button>

            <div className="sidebar-desktop-actions">
              <div className="header-icon-wrapper notification-wrapper">
                <NotificationBell />
              </div>

              {user.role === 'SUPER_ADMIN' && (
                <div className="header-icon-wrapper online-users-badge-wrapper">
                  <OnlineUsersBadge />
                </div>
              )}

              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`action-btn sidebar-refresh-btn ${isRefreshing ? 'refreshing' : ''}`}
                title="Làm mới trang"
              >
                <FaSync className={isRefreshing ? 'fa-spin' : ''} />
              </button>

            </div>
          </div>
        )}

        <div className="sidebar-header">
          <h3>Menu điều hướng</h3>
          <button
            className="sidebar-close"
            onClick={() => {
              setSidebarOpen(false);
              setExpandedGroups({});
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Search */}
        <div className="sidebar-search">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Tìm kiếm menu... (Ctrl+K)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
                title="Xóa tìm kiếm (ESC)"
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        <div className="sidebar-content">
          {/* Search Results Info */}
          {searchQuery && (
            <div className="search-results-info">
              <i className="fas fa-search"></i>
              <span>Tìm kiếm: "{searchQuery}"</span>
            </div>
          )}

          {/* Common Links - Always Visible */}
          {(() => {
            const q = normalizeText(searchQuery);
            const filteredCommonLinks = commonLinks.filter(link =>
              !searchQuery || normalizeText(link.label).includes(q)
            );

            if (searchQuery && filteredCommonLinks.length === 0) return null;

            return (
              <div className="nav-section common-section">
                <h4 className="section-title static">
                  Chung
                  {searchQuery && <span className="search-count">({filteredCommonLinks.length})</span>}
                </h4>
                <div className="nav-items always-visible">
                  {filteredCommonLinks.map((link, index) => (
                    <button
                      key={index}
                      onClick={(e) => handleNavigation(link.to, e)}
                      className={`nav-item ${location.pathname === link.to ? 'active' : ''}`}
                      title={link.label}
                    >
                      <i className={`fas ${link.icon} nav-icon`}></i>
                      <span>{link.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* User-specific Navigation */}
          {userNavigation.map((group, groupIndex) => {
            const q = normalizeText(searchQuery || '');
            const filteredItems = filterItems(group.items, searchQuery);
            const matchesGroupTitle = !!searchQuery && normalizeText(group.title).includes(q);
            const shouldShowGroup = !searchQuery || matchesGroupTitle || filteredItems.length > 0;

            if (!shouldShowGroup) return null;

            const itemsToRender = searchQuery
              ? (matchesGroupTitle ? group.items : filteredItems)
              : group.items;

            const displayCount = searchQuery
              ? (matchesGroupTitle ? group.items.length : filteredItems.length)
              : group.items.length;

            return (
              <div key={groupIndex} className="nav-section">
                <button
                  className={`section-title expandable ${expandedGroups[groupIndex] ? 'expanded' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isDesktopMiniMode && !sidebarOpen) {
                      setSidebarOpen(true);
                      setExpandedGroups({ [groupIndex]: true });
                      return;
                    }
                    toggleGroup(groupIndex);
                  }}
                  title={group.title}
                >
                  <div className="section-title-content">
                    <i className={`fas ${group.icon} section-icon`} style={{ color: group.color }}></i>
                    <span>{group.title}</span>
                    {searchQuery && (
                      <span className="search-count">({displayCount})</span>
                    )}
                  </div>
                  <FaChevronRight className={`chevron ${expandedGroups[groupIndex] ? 'rotated' : ''}`} />
                </button>

                <div className="nav-items">
                  {itemsToRender.map((item, itemIndex) => (
                    <button
                      key={itemIndex}
                      onClick={(e) => handleNavigation(item.to, e)}
                      className={`nav-item ${location.pathname === item.to ? 'active' : ''}`}
                      title={item.label}
                    >
                      <i className={`fas ${item.icon} nav-icon`}></i>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}



          {/* No Results Message */}
          {searchQuery && (() => {
            const q = normalizeText(searchQuery);
            const hasCommonResults = commonLinks.some(link =>
              normalizeText(link.label).includes(q)
            );
            const hasUserResults = userNavigation.some(group =>
              normalizeText(group.title).includes(q) ||
              group.items.some(item => normalizeText(item.label).includes(q))
            );

            const hasAnyResults = hasCommonResults || hasUserResults;

            if (!hasAnyResults) {
              return (
                <div className="no-search-results">
                  <i className="fas fa-search"></i>
                  <p>Không tìm thấy kết quả nào cho "{searchQuery}"</p>
                  <small>Thử tìm kiếm với từ khóa khác</small>
                </div>
              );
            }
            return null;
          })()}

          {/* Bottom Spacing */}
          <div className="sidebar-bottom-spacing"></div>
        </div>

        {isDesktopMiniMode && user && sidebarOpen && (
          <div className="sidebar-open-user-wrap">
            <div className="user-menu-container sidebar-user-menu" ref={userMenuRef}>
              <button
                className="user-avatar-btn"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                {user.avatar ? (
                  <img src={user.avatar} alt="Avatar" className="user-avatar" />
                ) : (
                  <div className="user-avatar-placeholder">
                    {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                  </div>
                )}
                <span className="user-name">{user.fullName || 'User'}</span>
                <FaChevronDown className={`chevron ${userMenuOpen ? 'open' : ''}`} />
              </button>

              {renderUserDropdown('sidebar')}
            </div>
          </div>
        )}

        {isDesktopMiniMode && user && !sidebarOpen && (
          <div className="sidebar-collapsed-user-wrap" ref={userMenuRef}>
            <button
              className="sidebar-collapsed-user"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              title={user.fullName || 'User'}
              aria-label={user.fullName || 'User'}
            >
              {user.avatar ? (
                <img src={user.avatar} alt="Avatar" className="user-avatar" />
              ) : (
                <div className="user-avatar-placeholder">
                  {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                </div>
              )}
            </button>
            {renderUserDropdown('sidebar-collapsed')}
          </div>
        )}
      </nav>
    </div>
  );
};

export default ModernNavbar;
